// eslint-disable-next-line @typescript-eslint/triple-slash-reference
/// <reference path="../node_modules/monaco-editor/monaco.d.ts" />
import React, { useEffect, useImperativeHandle, useRef, Suspense, lazy } from "react";
import { OnMount, useMonaco } from "@monaco-editor/react";
import PubSub from "pubsub-js";
import { InfoveaveTheme, SciPyR } from "./themes";

const Editor = lazy(() => import("@monaco-editor/react"));

const mdxMonarchLanguage: monaco.languages.IMonarchLanguage = {
  tokenizer: {
    root: [[/((\.)?(\[([A-Za-z 0-9._-])+\])|Children)/, "keyword"]],
  },
  tokenPostfix: "",
};

const parserMonarchLanguage: monaco.languages.IMonarchLanguage = {
  tokenizer: {
    root: [
      [/(RECORD|COLUMN|SUBRECORD|IGNORE|LINE|NEXT|EMPTYLINE|TILL|SKIP)/, "keyword"],
      [/(BEGIN|END)/, "action"],
      [/#([A-Za-z0-9 <>()~!@#$%^&*()_+`={}[\]:";'?,./|\\-])+/, "comment"],
      [/ [0-9 <>()~!@#$%^&*()_+`={}[\]:";'?,./|\\-]+/, "identifier"],
    ],
  },
  tokenPostfix: "",
};

interface CompletionRequest {
  model: monaco.editor.ITextModel;
  position: monaco.Position;
  context: monaco.languages.CompletionContext;
  token: monaco.CancellationToken;
}

export interface BaseEditorProps {
  width: number | string;
  height: number | string;
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  theme?: string;
  onReady?: (editor: monaco.editor.IStandaloneCodeEditor) => void;
}

export interface MonacoEditorProps extends BaseEditorProps {
  language:
    | "infoveave-sql"
    | "infoveave-mdx"
    | "infoveave-markdown"
    | "infoveave-r"
    | "infoveave-python"
    | "infoveave-text"
    | "infoveave-javascript"
    | "infoveave-json"
    | "infoveave-parser"
    | "infoveave-whatif"
    | "infoveave-questions"
    | "infoveave-html";
  autoComplete: (
    model: monaco.editor.ITextModel,
    position: monaco.Position,
    context: monaco.languages.CompletionContext,
    token: monaco.CancellationToken
  ) => Promise<monaco.languages.ProviderResult<monaco.languages.CompletionList>>;
  options?: monaco.editor.IStandaloneEditorConstructionOptions;
  className?: string;
}

export interface MonacoEditorHandle {
  editor: monaco.editor.IStandaloneCodeEditor;
}

const getCompletionItemProvider = (language: string, triggerCharacters?: string[]): monaco.languages.CompletionItemProvider => {
  return {
    provideCompletionItems: (model, position, context, token) => {
      return new Promise((resolve) => {
        PubSub.subscribe(
          `CodeEditor:CompletionItemsResponse:${language}`,
          (message: string, data: monaco.languages.ProviderResult<monaco.languages.CompletionList>) => {
            resolve(data);
          }
        );
        PubSub.publish(`CodeEditor:RequestCompletionItems:${language}`, { model, position, context, token });
      });
    },
    triggerCharacters: triggerCharacters,
  };
};

export const MonacoLoader = () => {
  const monaco = useMonaco();
  useEffect(() => {
    if (!monaco) {
      return;
    }
    monaco.languages.register({ id: "parser" });
    monaco.languages.register({ id: "whatif" });
    monaco.languages.register({ id: "questions" });
    monaco.languages.register({ id: "mdx" });
    monaco.languages.setMonarchTokensProvider("parser", parserMonarchLanguage);
    monaco.languages.setMonarchTokensProvider("mdx", mdxMonarchLanguage);
    monaco.languages.registerCompletionItemProvider("sql", getCompletionItemProvider("sql", ["@", "."]));
    monaco.languages.registerCompletionItemProvider("markdown", getCompletionItemProvider("markdown", ["@"]));
    monaco.languages.registerCompletionItemProvider("r", getCompletionItemProvider("r", ["(", ",", "$", "."]));
    monaco.languages.registerCompletionItemProvider("python", getCompletionItemProvider("python", ["(", ",", "."]));
    monaco.languages.registerCompletionItemProvider("javascript", getCompletionItemProvider("javascript", ["@"]));
    monaco.languages.registerCompletionItemProvider("json", getCompletionItemProvider("json", []));
    monaco.languages.registerCompletionItemProvider("parser", getCompletionItemProvider("parser", []));
    monaco.languages.registerCompletionItemProvider("whatif", getCompletionItemProvider("whatif", []));
    monaco.languages.registerCompletionItemProvider("questions", getCompletionItemProvider("questions", []));
    monaco.languages.registerCompletionItemProvider("mdx", getCompletionItemProvider("mdx", []));
    monaco.editor.defineTheme(`InfoveaveTheme`, InfoveaveTheme);
    monaco.editor.defineTheme(`SciPyR`, SciPyR);
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: true,
      noSuggestionDiagnostics: true,
    });
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES5,
      noLib: true,
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).MonacoLoaded = true;
    return () => {
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:sql`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:markdown`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:r`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:python`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:javascript`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:json`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:parser`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:whatif`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:questions`);
      PubSub.unsubscribe(`CodeEditor:CompletionItemsResponse:mdx`);
    };
  }, [monaco]);
  return <noscript />;
};

export const MonacoEditor = React.forwardRef<MonacoEditorHandle, MonacoEditorProps>(function MonacoEditor(props, ref) {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  /*
    useEffect(() => {
        window.addEventListener("resize", handleWidowResize);
        return () => {
            window.removeEventListener("resize", handleWidowResize);
        }
    }, []);

    const handleWidowResize = () => {
        editorRef.current?.layout();
    }
    */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEditorDidMount: OnMount = (instance: any) => {
    editorRef.current = instance;
    instance.addAction({
      id: "c-undo",
      label: "Undo",
      // tslint:disable-next-line: no-bitwise
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ],
      keybindingContext: undefined,
      run: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((instance as monaco.editor.IStandaloneCodeEditor).getModel() as any).undo();
        //ed.trigger("me", "undo", null);
      },
    });
    instance.addAction({
      id: "c-redo",
      label: "Undo",
      // tslint:disable-next-line: no-bitwise
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyZ],
      keybindingContext: undefined,
      run: () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((instance as monaco.editor.IStandaloneCodeEditor).getModel() as any).redo();
      },
    });
    if (props.onReady) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props.onReady(instance as any);
    }
  };

  useEffect(() => {
    PubSub.subscribe(`CodeEditor:RequestCompletionItems:${props.language.replace("infoveave-", "")}`, (message: string, data: CompletionRequest) => {
      if (!props.autoComplete) {
        PubSub.publish(`CodeEditor:CompletionItemsResponse:${props.language.replace("infoveave-", "")}`, {
          suggestions: [],
        });
      } else {
        props.autoComplete(data.model, data.position, data.context, data.token).then((result) => {
          PubSub.publish(`CodeEditor:CompletionItemsResponse:${props.language.replace("infoveave-", "")}`, result);
        });
      }
    });
    return () => {
      PubSub.unsubscribe(`CodeEditor:RequestCompletionItems:${props.language.replace("infoveave-", "")}`);
    };
  }, [props.autoComplete]);

  useImperativeHandle(ref, () => {
    return {
      editor: editorRef.current as monaco.editor.IStandaloneCodeEditor,
    };
  });
  //need to check
  const handleEditorChange = (value: string | undefined, ev: monaco.editor.IModelContentChangedEvent) => {
    if (props.onChange) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((ev.changes[0] as any).forceMoveMarkers === true) return;
      props.onChange(value ?? "");
    }
  };

  const { width, height } = props;
  const fixedWidth = width.toString().indexOf("%") !== -1 ? width : `${width}px`;
  const fixedHeight = height.toString().indexOf("%") !== -1 || height.toString().includes("calc") ? height : `${height}px`;
  return (
    <Suspense fallback={<div>loading...</div>}>
      <Editor
        className={props.className}
        onMount={handleEditorDidMount}
        width={fixedWidth}
        height={fixedHeight}
        value={props.value}
        onChange={handleEditorChange}
        options={{
          automaticLayout: true,
          lineNumbers: "off",
          minimap: {
            enabled: false,
          },
          padding: { top: 2, bottom: 2 },

          glyphMargin: false,
          codeLens: false,
          wordWrap: "on",
          scrollBeyondLastLine: false,
          fontSize: 14,
          readOnly: props.readOnly,
          ...props.options,
        }}
        language={props.language.replace("infoveave-", "")}
        theme={props.theme ? props.theme : "InfoveaveTheme"}
      />
    </Suspense>
  );
});
